import { GraphConfig, GraphVariant } from '@ntv360/component-pantry';

/**
 * Parameters for building a shared graph configuration.
 */
export interface GraphHelperParams {
    /**
     * Data object containing cumulative and monthly values along with series categories.
     */
    data: {
        /** Cumulative data values over time. */
        cumulative: number[];
        /** Individual monthly data values. */
        monthly: number[];
        /** Labels for the X-axis categories. */
        categories: string[];
    };
    /**
     * An array containing the names for the two data series.
     * Index 0 corresponds to cumulative data, and Index 1 corresponds to monthly data.
     */
    seriesNames: [string, string];
    /**
     * The visual variant of the graph.
     * @default GraphVariant.LineWithLegend
     */
    variant?: GraphVariant;
    /**
     * Optional padding configuration for the graph plot area.
     */
    padding?: { left: number; right: number };
}

/**
 * Builds a standardized GraphConfig object for line graphs across the application.
 * This utility configures consistent colors, stroke widths, markers, axes formatting,
 * and tooltips for comparison graphs.
 *
 * @param params - The parameters to build the graph configuration.
 * @returns A standardized GraphConfig object.
 */
export function buildSharedGraphConfig(params: GraphHelperParams): GraphConfig {
    return {
        variant: params.variant ?? GraphVariant.LineWithLegend,
        size: 'small',
        series: [
            { name: params.seriesNames[0], data: params.data.cumulative },
            { name: params.seriesNames[1], data: params.data.monthly },
        ],
        colors: ['#8DCB2C', '#1e88f5'],
        categories: params.data.categories,
        options: {
            stroke: { width: [5, 4], curve: 'straight' },
            legend: { show: false },
            grid: {
                borderColor: '#f1f1f1',
                strokeDashArray: 4,
                xaxis: { lines: { show: false } },
                yaxis: { lines: { show: true } },
                ...(params.padding && { padding: params.padding }),
            },
            markers: {
                size: 0,
                hover: { size: 5, strokeWidth: 3, colors: ['#fff'] },
            },
            yaxis: {
                labels: {
                    formatter: (val: number) => val.toLocaleString(),
                    style: { colors: '#94a3b8', fontSize: '11px' },
                },
                tickAmount: 5,
            },
            xaxis: {
                labels: {
                    style: { colors: '#94a3b8', fontSize: '11px' },
                    rotate: 0,
                },
            },
            tooltip: {
                theme: 'light',
                x: { show: true, formatter: () => 'Comparison' },
                y: { formatter: (val: number) => val.toLocaleString() },
            },
        },
    } as GraphConfig;
}
